/// <reference types="vite/client" />
/**
 * Google Analytics 4 web adapter.
 * Uses direct Google tag (gtag.js) — not Google Tag Manager.
 */

import {
  type CommonParams,
  type EventName,
  type EventParamsMap,
  normalizeRoute,
  redactUrl,
  shouldTrack,
} from '@trade-connector/shared';
import { getReferralCode } from '../referralStorage';
import { getTeaserToken } from '../teaserStorage';
import type { User } from '../../contexts/auth.types';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
  interface ImportMetaEnv {
    readonly VITE_GA_MEASUREMENT_ID?: string;
    readonly VITE_STRIPE_PUBLISHABLE_KEY: string;
  }
}

let isInitialized = false;
let currentUser: User | null = null;
let previousHref = '';
let lastPageViewHref = '';

// Pages such as /signup/client call track() in a mount effect, before auth has resolved, when it is
// not yet known whether the user is excluded. Those events wait here until setAnalyticsUser runs
// (Layout calls it once auth has resolved), which sends or drops each one. They are held, not
// dropped, because nearly every cold visit fires its first event before auth resolves.
const MAX_HELD_EVENTS = 20;
let authResolved = false;
let heldEvents: Array<{ event: EventName; params: object; pathname: string }> = [];

export function isAnalyticsActive(): boolean {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (!measurementId || typeof measurementId !== 'string' || !measurementId.trim()) {
    return false;
  }
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return false;
  }
  return true;
}

export function getSignupEntry(): 'direct' | 'referral' | 'teaser' | 'crm_invite' {
  if (typeof window === 'undefined') return 'direct';
  try {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('teaser') || getTeaserToken()) {
      return 'teaser';
    }
    if (urlParams.get('crm_lead_id') || localStorage.getItem('tc_crm_lead_id')) {
      return 'crm_invite';
    }
    if (urlParams.get('ref') || getReferralCode()) {
      return 'referral';
    }
  } catch {
    // Fall back to direct on storage errors
  }
  return 'direct';
}

export function getUserRole(user: User): 'client' | 'trade' | 'dual' {
  if (user.account_type === 'trade_company') return 'trade';
  if (user.account_type === 'dual') return 'dual';
  return 'client';
}

export function getUserActiveSide(user: User): 'client' | 'trade' | 'platform' {
  if (user.active_role === 'trade_company') return 'trade';
  if (user.active_role === 'platform') return 'platform';
  if (user.active_role === 'customer') return 'client';
  return getUserRole(user) === 'trade' ? 'trade' : 'client';
}

export function getUserAccountStatus(user: User): 'pending' | 'verified' | 'rejected' | 'other' {
  const rawStatus =
    user.active_role === 'trade_company'
      ? user.trade_company_status
      : user.customer_status || user.trade_company_status;

  if (rawStatus === 'ACTIVE' || rawStatus === 'VERIFIED') return 'verified';
  if (rawStatus === 'PENDING' || rawStatus === 'PENDING_VERIFICATION') return 'pending';
  if (rawStatus === 'REJECTED') return 'rejected';
  return 'other';
}

/**
 * Initializes the gtag runtime and injects the Google tag script.
 * Silent no-op when inactive.
 */
export function initAnalytics(): void {
  if (isInitialized) return;
  if (!isAnalyticsActive()) return;

  const measurementId = (import.meta.env.VITE_GA_MEASUREMENT_ID || '').trim();
  if (!measurementId) return;

  try {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function () {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer?.push(arguments);
      };
    }

    if (typeof document !== 'undefined') {
      const scriptSrc = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);
      if (!existingScript) {
        const script = document.createElement('script');
        script.async = true;
        script.src = scriptSrc;
        document.head.appendChild(script);
      }
    }

    window.gtag('js', new Date());

    const initialLocation = redactUrl(window.location.href);
    const initialReferrer = document.referrer ? redactUrl(document.referrer) : '';
    const initialTitle = normalizeRoute(window.location.pathname);

    // The page fields go through `set`, never `config`: gtag gives a config-level value priority over
    // every later `set`, so scroll and track() events after an in-app navigation would keep the
    // landing page. trackPageView updates them with `set` on each navigation.
    window.gtag('set', {
      page_location: initialLocation,
      page_referrer: initialReferrer,
      page_title: initialTitle,
    });
    window.gtag('config', measurementId, { send_page_view: false });

    isInitialized = true;
  } catch (err) {
    console.debug('[analytics] init error', err);
  }
}

/**
 * Sets Google's documented opt-out flag (`window['ga-disable-<ID>']`), which gtag.js checks on every
 * hit. This is what stops the events gtag sends by itself (enhanced-measurement scrolls), which never
 * pass through track()/trackPageView(). Without it, excluded users and /admin pages would still send
 * anonymous scroll events, and each one counts as a visitor.
 */
function setCollectionEnabled(enabled: boolean): void {
  const measurementId = (import.meta.env.VITE_GA_MEASUREMENT_ID || '').trim();
  if (!measurementId) return;
  (window as unknown as Record<string, boolean>)[`ga-disable-${measurementId}`] = !enabled;
}

/**
 * Syncs user identity with GA4. Sets user_id and user properties on login;
 * clears them on logout and for excluded users (and disables collection for them).
 */
export function setAnalyticsUser(user: User | null): void {
  currentUser = user;
  authResolved = true;
  if (!isAnalyticsActive()) return;

  const canTrack = shouldTrack({
    isPlatformAdmin: Boolean(user?.is_platform_admin),
    isImpersonating: Boolean(user?.impersonated_by_admin_id),
    isAnalyticsExcluded: Boolean(user?.is_analytics_excluded),
    pathname: window.location.pathname,
  });
  setCollectionEnabled(canTrack);

  initAnalytics();

  try {
    if (!user) {
      if (window.gtag) {
        window.gtag('set', { user_id: null });
        window.gtag('set', 'user_properties', {
          user_role: null,
          active_side: null,
          account_status: null,
        });
      }
      return;
    }

    if (!canTrack) {
      if (window.gtag) {
        window.gtag('set', { user_id: null });
        window.gtag('set', 'user_properties', {
          user_role: null,
          active_side: null,
          account_status: null,
        });
      }
      return;
    }

    const userRole = getUserRole(user);
    const activeSide = getUserActiveSide(user);
    const accountStatus = getUserAccountStatus(user);

    if (window.gtag) {
      window.gtag('set', { user_id: user.id });
      window.gtag('set', 'user_properties', {
        user_role: userRole,
        active_side: activeSide,
        account_status: accountStatus,
      });
    }
  } catch (err) {
    console.debug('[analytics] set user error', err);
  } finally {
    // After the identity above, so held events carry this user's user_id (or are dropped).
    flushHeldEvents();
  }
}

function flushHeldEvents(): void {
  const held = heldEvents;
  heldEvents = [];
  for (const { event, params, pathname } of held) {
    sendEvent(event, params, pathname);
  }
}

/**
 * Tracks a page view with redacted URL parameters and normalised route name.
 */
export function trackPageView(pathname?: string, href?: string): void {
  if (!isAnalyticsActive()) return;

  const currentHref = href ?? window.location.href;
  if (currentHref === lastPageViewHref) {
    return;
  }
  lastPageViewHref = currentHref;

  const currentPathname = pathname ?? window.location.pathname;

  const canTrack = shouldTrack({
    isPlatformAdmin: Boolean(currentUser?.is_platform_admin),
    isImpersonating: Boolean(currentUser?.impersonated_by_admin_id),
    isAnalyticsExcluded: Boolean(currentUser?.is_analytics_excluded),
    pathname: currentPathname,
  });
  setCollectionEnabled(canTrack);

  initAnalytics();

  if (!canTrack) {
    previousHref = currentHref;
    return;
  }

  try {
    const redactedLocation = redactUrl(currentHref);
    const redactedReferrer = previousHref
      ? redactUrl(previousHref)
      : document.referrer
        ? redactUrl(document.referrer)
        : '';
    const redactedTitle = normalizeRoute(currentPathname);

    previousHref = currentHref;

    if (window.gtag) {
      window.gtag('set', {
        page_location: redactedLocation,
        page_referrer: redactedReferrer,
        page_title: redactedTitle,
      });

      window.gtag('event', 'page_view', {
        page_location: redactedLocation,
        page_referrer: redactedReferrer,
        page_title: redactedTitle,
        platform: 'web',
        ...(currentUser ? { user_role: getUserRole(currentUser) } : {}),
      });
    }
  } catch (err) {
    console.debug('[analytics] page view error', err);
  }
}

/**
 * Tracks a custom event from the typed event catalogue.
 */
export function track<E extends EventName>(
  event: E,
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  ...args: [{}] extends [EventParamsMap[E]]
    ? [params?: EventParamsMap[E]]
    : [params: EventParamsMap[E]]
): void {
  if (!isAnalyticsActive()) return;

  const params: object = { ...(args[0] || {}) };
  const pathname = window.location.pathname;
  if (!authResolved) {
    if (heldEvents.length < MAX_HELD_EVENTS) {
      // The page can change before the flush (AuthCallbackPage tracks, then navigates to /dashboard),
      // so a held event carries the page it was tracked on, redacted exactly as trackPageView does.
      // The page fields go last, so a caller's params can never replace them with an unredacted URL.
      const pageLocation = redactUrl(window.location.href);
      heldEvents.push({
        event,
        params: {
          ...params,
          ...(pageLocation ? { page_location: pageLocation } : {}),
          page_title: normalizeRoute(pathname),
        },
        pathname,
      });
    }
    return;
  }
  sendEvent(event, params, pathname);
}

function sendEvent(event: EventName, params: object, pathname: string): void {
  const canTrack = shouldTrack({
    isPlatformAdmin: Boolean(currentUser?.is_platform_admin),
    isImpersonating: Boolean(currentUser?.impersonated_by_admin_id),
    isAnalyticsExcluded: Boolean(currentUser?.is_analytics_excluded),
    pathname,
  });

  if (!canTrack) return;

  try {
    initAnalytics();

    const commonParams: CommonParams = {
      platform: 'web',
      ...(currentUser ? { user_role: getUserRole(currentUser) } : {}),
    };

    const payload = {
      ...commonParams,
      ...params,
    };

    if (window.gtag) {
      window.gtag('event', event, payload);
    }
  } catch (err) {
    console.debug('[analytics] track error', err);
  }
}

/** Reset internal module state for isolated tests */
export function _resetForTesting(): void {
  isInitialized = false;
  currentUser = null;
  previousHref = '';
  lastPageViewHref = '';
  authResolved = false;
  heldEvents = [];
  for (const key of Object.keys(window)) {
    if (key.startsWith('ga-disable-')) delete (window as unknown as Record<string, unknown>)[key];
  }
}
